import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root'
})
export class PortalVersionService {
  private readonly MAX_RELOAD_ATTEMPTS = 5;
  private readonly COOKIE_MAX_AGE = 60; // 1 minute
  private readonly RELOAD_COOKIE_MAX_AGE = 60; // 1 minute

  constructor(private http: HttpClient) {}

  /**
   * Checks portal version and reloads if necessary
   * Returns true if version is OK, false if reload is needed
   */
  async checkAndReloadIfNeeded(): Promise<boolean> {
    try {
      const versionStatus = await this.checkPortalVersion();
      
      if (versionStatus === 'NOK') {
        await this.forceReload();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Version check failed:', error);
      // If version check completely fails, still allow app to load
      return true;
    }
  }

  private async checkPortalVersion(): Promise<'OK' | 'NOK'> {
    try {
      // Check if we're in the cookie reload cycle
      const isReloading = this.getCookie('version-reloading') !== null;
      
      // Check retry count to prevent infinite loops
      const reloadCount = this.getReloadCount();
      
      // If we've reloaded 5 times, something is wrong with the api
      if (reloadCount >= this.MAX_RELOAD_ATTEMPTS) {
        console.warn('Version check: Maximum reload attempts reached. Loading app anyway.');
        this.clearVersionCookies();
        return 'OK';
      }
      
      const version = await this.fetchPortalVersion();
      
      if (!version) {
        console.error('Failed to fetch version');
        this.incrementReloadCount(reloadCount);
        return 'NOK';
      }

      const versionString = String(version);
      const localPortalVersion = localStorage.getItem('portal-version');
      
      // If we just reloaded successfully, save version and clear cookies
      if (isReloading) {
        localStorage.setItem('portal-version', versionString);
        this.clearVersionCookies();
        return 'OK';
      }

      // No local version or version mismatch: force reload
      if (!localPortalVersion || versionString !== localPortalVersion) {
        this.incrementReloadCount(reloadCount);
        return 'NOK';
      }

      // Versions match: clear reload count and continue
      this.deleteCookie('version-reload-count');
      return 'OK';

    } catch (error) {
      console.error('Error checking portal version:', error);
      const reloadCount = this.getReloadCount();
      
      if (reloadCount >= this.MAX_RELOAD_ATTEMPTS) {
        console.warn('Version check: Maximum reload attempts reached after error. Loading app anyway.');
        this.deleteCookie('version-reload-count');
        return 'OK';
      }
      
      this.incrementReloadCount(reloadCount);
      return 'NOK';
    }
  }

  private async fetchPortalVersion(): Promise<any> {
    try {
      // Force bypass all caches with timestamp and strong headers
      const timestamp = new Date().getTime();
      const url = `${environment.backend_api_url}/api/Portal/version?nocache=${timestamp}`;
      
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      });

      const response = await this.http.get(
        url,
        { headers }
      ).toPromise();

      return response;
    } catch (error) {
      console.error('Failed to fetch portal version:', error);
      return null;
    }
  }

  private async forceReload(): Promise<void> {
    // Set reload flag BEFORE clearing storage so it persists
    this.setCookie('version-reloading', 'true', this.RELOAD_COOKIE_MAX_AGE);
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear all cookies except version-reloading and version-reload-count
    document.cookie.split(";").forEach(cookie => {
      const cookieName = cookie.split("=")[0].trim();
      if (!cookieName.startsWith('version-reloading') && !cookieName.startsWith('version-reload-count')) {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        document.cookie = `${cookieName}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    });
    
    // Clear all service worker caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Cache cleared successfully');
      } catch (error) {
        console.error('Error clearing caches:', error);
      }
    }
    
    // Small delay to ensure cookies are set before reload
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Force hard reload with aggressive cache bypass
    (window as any).location.reload();
  }

  private getReloadCount(): number {
    const cookie = this.getCookie('version-reload-count');
    return cookie ? parseInt(cookie) : 0;
  }

  private incrementReloadCount(currentCount: number): void {
    this.setCookie('version-reload-count', String(currentCount + 1), this.COOKIE_MAX_AGE);
  }

  private clearVersionCookies(): void {
    this.deleteCookie('version-reloading');
    this.deleteCookie('version-reload-count');
  }

  private getCookie(name: string): string | null {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`));
    
    return cookie ? cookie.split('=')[1] : null;
  }

  private setCookie(name: string, value: string, maxAge: number): void {
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}`;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; path=/; max-age=0`;
  }
}